import type { UnderwayActivity } from './types';

// Kind-based headings for the underway row (PM copy matrix, 2026-08-10). The
// heading is generic — "Sending email(s)", "Interview scheduled" — while the
// activity NAME lives only in the description sentence. External providers
// collapse onto the core noun (HackerRank → "assessment"), mirroring the
// builder's PARENT_NOUN_BY_KIND.

type HeadingNoun = 'email' | 'interview' | 'assessment' | 'review' | 'survey';

const NOUN_BY_KIND: Record<string, HeadingNoun> = {
  email: 'email',
  interview: 'interview',
  hackerrank_interview: 'interview',
  assessment: 'assessment',
  hackerrank: 'assessment',
  hackerearth_assessment: 'assessment',
  criteriacorp_assessment: 'assessment',
  codeinterview_assessment: 'assessment',
  hireflix_assessment: 'assessment',
  review: 'review',
  survey: 'survey',
};

const capitalize = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The row's heading, per the matrix:
 * - immediate, during (pending/paused/running): "Sending email(s)" /
 *   "Scheduling interview(s)"
 * - immediate, after (completed): "Email(s) sent" / "Interview(s) scheduled"
 * - delayed, during: "Queuing email(s)"
 * - delayed, after: "Email(s) queued"
 * Returns null for states the matrix doesn't cover (failed / cancelled) —
 * callers keep their label-based error copy there.
 */
export const getUnderwayHeading = (
  activity: Pick<
    UnderwayActivity,
    'activityKind' | 'isBulk' | 'delayed' | 'status'
  >
): string | null => {
  const { status } = activity;
  if (status === 'failed' || status === 'cancelled') return null;

  const baseNoun = NOUN_BY_KIND[activity.activityKind] ?? 'email';
  const noun = activity.isBulk ? `${baseNoun}s` : baseNoun;
  const after = status === 'completed';

  if (activity.delayed) {
    return after ? `${capitalize(noun)} queued` : `Queuing ${noun}`;
  }
  if (baseNoun === 'interview') {
    return after ? `${capitalize(noun)} scheduled` : `Scheduling ${noun}`;
  }
  return after ? `${capitalize(noun)} sent` : `Sending ${noun}`;
};
